/**
 * Header chính của layout công khai (PublicLayout).
 *
 * Gồm logo, thanh tìm kiếm HeaderSearch, yêu thích và menu người dùng.
 * Bộ lọc mặc định được đồng bộ từ query string URL hiện tại.
 */
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HeaderUserMenuPanel } from './HeaderUserMenuPanel';
import { useLoginDialog } from '../../hooks/useLoginDialog';
import { useRegisterDialog } from '../../hooks/useRegisterDialog';
import { useHeaderUserMenu } from '../../hooks/useHeaderUserMenu';
import { TEXT } from '../../shared/languages';
import { parsePropertySearchParams } from '../../shared/utils/propertySearchParams';
import { HeaderSearch } from './HeaderSearch';
import { APP_ROUTES } from '../../shared/constants/routes';
import './HeaderUserMenu.css';
import './Header.css';

/** Header site với tìm kiếm và menu tài khoản. */
export const Header = () => {
  const { openLoginDialog } = useLoginDialog();
  const { openRegisterDialog } = useRegisterDialog();
  const location = useLocation();
  const {
    user,
    isAuthenticated,
    isLoggingOut,
    isMenuOpen,
    setIsMenuOpen,
    menuRef,
    closeMenu,
    handleLogout,
    canAccessAdmin,
  } = useHeaderUserMenu();
  const nav = TEXT.nav;

  const defaultFilters = useMemo(
    () => parsePropertySearchParams(new URLSearchParams(location.search)),
    [location.search]
  );

  return (
    <header className="header">
      <div className="header-content">
        <Link to={APP_ROUTES.home} className="logo" onClick={closeMenu}>
          <span className="logo-mark" aria-hidden="true">
            <svg viewBox="0 0 32 32" fill="currentColor">
              <path d="M16 3.5c-1.2 3.8-4.8 6.4-8.7 6.4-.3 0-.6 0-.9-.1 2.8 2.1 6.3 3.4 10.1 3.4 3.8 0 7.3-1.3 10.1-3.4-.3 0-.6.1-.9.1-3.9 0-7.5-2.6-8.7-6.4z" />
              <path d="M16 28.5c1.2-3.8 4.8-6.4 8.7-6.4.3 0 .6 0 .9.1-2.8-2.1-6.3-3.4-10.1-3.4-3.8 0-7.3 1.3-10.1 3.4.3 0 .6-.1.9-.1 3.9 0 7.5 2.6 8.7 6.4z" />
            </svg>
          </span>
          <strong>{TEXT.common.brandName}</strong>
        </Link>

        <HeaderSearch defaultFilters={defaultFilters} variant="wide" />

        <div className="header-actions">
          {isAuthenticated && (
            <Link to={APP_ROUTES.favorites} className="header-wishlist" aria-label={nav.favorites}>
              <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 28c-.2 0-.4-.1-.5-.2C7.5 21.1 2 16.2 2 10.5 2 6.4 5.4 3 9.5 3c2.4 0 4.7 1.2 6.1 3.1C17 4.2 19.3 3 21.7 3 25.8 3 29 6.4 29 10.5c0 5.7-5.5 10.6-13.5 17.3-.1.1-.3.2-.5.2z" />
              </svg>
            </Link>
          )}

          <div className={`site-user-menu ${isMenuOpen ? 'is-open' : ''}`} ref={menuRef}>
            <button
              type="button"
              className="site-user-menu-trigger"
              aria-expanded={isMenuOpen}
              aria-haspopup="true"
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              <svg className="site-user-menu-icon" viewBox="0 0 32 32" fill="currentColor">
                <path d="M2 8h28v2H2V8zm0 7h28v2H2v-2zm0 7h28v2H2v-2z" />
              </svg>
              <span className="site-user-menu-avatar">{user?.name?.charAt(0)?.toUpperCase() || '☺'}</span>
            </button>

            {isMenuOpen && (
              <HeaderUserMenuPanel
                isAuthenticated={isAuthenticated}
                user={user}
                canAccessAdmin={canAccessAdmin}
                isLoggingOut={isLoggingOut}
                closeMenu={closeMenu}
                onLogout={handleLogout}
                onOpenRegister={openRegisterDialog}
                onOpenLogin={openLoginDialog}
              />
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
